import type { ImportedAsset } from './import-types';

const MIME_TYPES: Record<string, string> = {
  css: 'text/css',
  js: 'text/javascript',
  mjs: 'text/javascript',
  json: 'application/json',
  txt: 'text/plain',
  xml: 'application/xml',

  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  avif: 'image/avif',

  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  otf: 'font/otf',

  pdf: 'application/pdf',

  mp4: 'video/mp4',
  webm: 'video/webm',

  mp3: 'audio/mpeg',
  wav: 'audio/wav',
};

export type AssetInventoryInput = {
  path: string;
  bytes: Uint8Array;
};

export function normalizeImportPath(inputPath: string): string {
  return inputPath
    .replaceAll('\\', '/')
    .replace(/^\.\/+/, '')
    .replace(/^\/+/, '')
    .replace(/\/{2,}/g, '/');
}

export function getFileExtension(filePath: string): string {
  const normalizedPath = normalizeImportPath(filePath);
  const fileName = normalizedPath.split('/').at(-1) ?? '';
  const dotIndex = fileName.lastIndexOf('.');

  if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
    return '';
  }

  return fileName.slice(dotIndex + 1).toLowerCase();
}

export function inferMimeType(filePath: string): string {
  const extension = getFileExtension(filePath);

  return MIME_TYPES[extension] ?? 'application/octet-stream';
}

export function createAssetId(filePath: string): string {
  const normalizedPath = normalizeImportPath(filePath);

  const slug = normalizedPath
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `asset-${slug || 'unknown'}`;
}

export function createImportedAsset(
  input: AssetInventoryInput,
): ImportedAsset {
  const normalizedPath = normalizeImportPath(input.path);

  return {
    id: createAssetId(normalizedPath),
    originalPath: input.path,
    normalizedPath,
    mimeType: inferMimeType(normalizedPath),
    size: input.bytes.byteLength,
    bytes: input.bytes,
  };
}

export function buildAssetInventory(
  inputs: AssetInventoryInput[],
): ImportedAsset[] {
  return inputs
    .map(createImportedAsset)
    .sort((left, right) =>
      left.normalizedPath.localeCompare(right.normalizedPath),
    );
}
